import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Swipeable from 'react-swipeable';
import { newOrder, addResistance } from '../ImageCarousel/ImageCarousel';

import BEMHelper from '@ynap/bem-helper';

const bem = new BEMHelper('ZoomedImageCarousel');

class ZoomedImageCarousel extends PureComponent {
    static defaultProps = {
        transition: 'transform 0.3s ease-out',
        transitionDuration: 300,
    };

    constructor(props) {
        super(props);
        const { slides, currentIndex } = this.props;

        const slidesWithoutVideo = slides.filter((slide) => !slide.video);
        this.imageRef = React.createRef();
        this.state = {
            slidesWithoutVideo,
            pageWidth: 100 / slidesWithoutVideo.length,
            trackWidth: slidesWithoutVideo.length * 100,
            diffIndex: 0,
            leftMarginNeeded: 0,
            delta: 0,
            animating: false,
            currentIndex: currentIndex,
            currentOrder: newOrder(slidesWithoutVideo, currentIndex),
        };
    }

    componentDidMount() {
        const { fx, fy } = this.props.zoomedPosition;

        const parentElement = this.imageRef.current.parentElement;
        const currentElement = this.imageRef.current;

        const x = fx * currentElement.offsetWidth;
        const y = fy * currentElement.offsetHeight - window.innerHeight / 2;

        parentElement.scrollTo(x, y);
        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress);
    }

    moveRight = (moveLength, currentIndex) => {
        this.setState(
            (prevState) => {
                return {
                    ...prevState,
                    currentIndex: currentIndex,
                    diffIndex: moveLength,
                    delta: 0,
                    leftMarginNeeded: 0,
                    animating: true,
                };
            },
            () => {
                setTimeout(() => {
                    this.setState((prevState) => {
                        return {
                            ...prevState,
                            diffIndex: 0,
                            animating: false,
                            currentOrder: newOrder(prevState.slidesWithoutVideo, prevState.currentIndex),
                        };
                    });
                }, this.props.transitionDuration);
            },
        );
    };

    moveLeft = (moveLength, currentIndex) => {
        this.setState(
            (prevState) => {
                return {
                    ...prevState,
                    currentIndex: currentIndex,
                    diffIndex: -moveLength,
                    leftMarginNeeded: -moveLength,
                    delta: 0,
                    animating: true,
                    currentOrder: newOrder(prevState.slidesWithoutVideo, currentIndex),
                };
            },
            () => {
                setTimeout(() => {
                    this.setState((prevState) => ({
                        ...prevState,
                        diffIndex: 0,
                        leftMarginNeeded: 0,
                        delta: 0,
                        animating: false,
                    }));
                }, this.props.transitionDuration);
            },
        );
    };

    slideTo(index, circular = false) {
        const { slidesWithoutVideo, animating } = this.state;

        if (animating) {
            return;
        }

        let currentIndex;

        currentIndex = index < 0 || index >= slidesWithoutVideo.length ? slidesWithoutVideo.length - Math.abs(index) : index;

        const currentIndexDiff =
            Math.abs(currentIndex - this.state.currentIndex) >= slidesWithoutVideo.length - 1 && circular
                ? currentIndex - this.state.currentIndex > 0
                    ? -1
                    : 1
                : currentIndex - this.state.currentIndex;

        if (currentIndexDiff > 0) {
            this.moveRight(Math.abs(currentIndexDiff), currentIndex);
        } else {
            this.moveLeft(Math.abs(currentIndexDiff), currentIndex);
        }
    }

    getSlideStyle = (first = false) => {
        const { pageWidth, leftMarginNeeded } = this.state;

        return {
            width: `${pageWidth}%`,
            marginLeft: first && leftMarginNeeded ? `${leftMarginNeeded * pageWidth}%` : 0,
        };
    };

    getTrackStyle = () => {
        const { transition, isRTL } = this.props;
        const { delta, trackWidth, pageWidth, diffIndex } = this.state;

        const transformNegative = isRTL ? '' : '-';

        return {
            width: `${trackWidth}%`,
            maxWidth: `${trackWidth}%`,
            transition: this.state.animating ? transition : 'none',
            transform:
                delta === 0
                    ? `translateX(${transformNegative}${pageWidth * (diffIndex + 2)}%)`
                    : `translateX(calc(${transformNegative}${pageWidth * (diffIndex + 2)}% + ${delta}px))`,
        };
    };

    prev = () => {
        const { currentIndex } = this.state;

        if (currentIndex > 0) {
            this.slideTo(currentIndex - 1, true);
            this.props.prev();
        }
    };

    next = () => {
        const { currentIndex, slidesWithoutVideo } = this.state;

        if (currentIndex < slidesWithoutVideo.length - 1) {
            this.slideTo(currentIndex + 1, true);
            this.props.next();
        }
    };

    handleSwipingLeft = (event, delta) => {
        const { currentIndex, slidesWithoutVideo } = this.state;
        const { isRTL } = this.props;

        if (isRTL ? currentIndex > 0 : currentIndex < slidesWithoutVideo.length - 1) {
            if (event.cancelable) {
                event.preventDefault();
            }
            this.setState({
                delta: -addResistance(delta),
            });
        }
    };

    handleSwipingRight = (event, delta) => {
        const { currentIndex, slidesWithoutVideo } = this.state;
        const { isRTL } = this.props;

        if (isRTL ? currentIndex < slidesWithoutVideo.length - 1 : currentIndex > 0) {
            if (event.cancelable) {
                event.preventDefault();
            }
            this.setState({
                delta: addResistance(delta),
            });
        }
    };

    handleSwiped = (event, deltaX, deltaY) => {
        const { currentIndex } = this.state;
        const { isRTL } = this.props;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
                return isRTL ? this.next() : this.prev();
            } else if (deltaX > 0) {
                return isRTL ? this.prev() : this.next();
            }
        }

        return this.slideTo(currentIndex, true);
    };

    handleKeyPress = (e) => {
        const { isRTL } = this.props;
        if (e.key === 'ArrowRight') {
            isRTL ? this.prev() : this.next();
        } else if (e.key === 'ArrowLeft') {
            isRTL ? this.next() : this.prev();
        }
    };

    render() {
        const { loading, closeZoomModal, isRTL } = this.props;
        const { animating, currentOrder, currentIndex, slidesWithoutVideo } = this.state;

        const skeleton = loading ? 'loading' : undefined;

        return (
            <div ref={this.imageRef} className={bem('zoomedCarousel')}>
                <button
                    className={bem('prev', [skeleton, isRTL ? 'isRTL' : undefined])}
                    type="button"
                    onClick={this.prev}
                    disabled={currentIndex <= 0}
                    aria-disabled={currentIndex <= 0}
                />

                <Swipeable
                    className={bem('viewport', skeleton)}
                    onSwipingLeft={this.handleSwipingLeft}
                    onSwipingRight={this.handleSwipingRight}
                    onSwiped={this.handleSwiped}
                    onClick={closeZoomModal}
                >
                    <ul
                        className={bem('track', skeleton)}
                        ref={(node) => {
                            this.trackRef = node;
                        }}
                        style={this.getTrackStyle()}
                    >
                        {currentOrder.map((slide, i) => (
                            <li key={`${slide.component.key}replica${slide.replica}`} className={bem('slide')} style={this.getSlideStyle(i === 0)}>
                                {slide.component}
                            </li>
                        ))}
                        {animating && [
                            <li
                                key={`${currentOrder[0].component.key}replica${currentOrder[0].replica}`}
                                className={bem('slide')}
                                style={{
                                    ...this.getSlideStyle(false),
                                }}
                            >
                                {currentOrder[0].component}
                            </li>,
                            <li
                                key={`${currentOrder[1].component.key}replica${currentOrder[1].replica}`}
                                className={bem('slide')}
                                style={{
                                    ...this.getSlideStyle(false),
                                }}
                            >
                                {currentOrder[1].component}
                            </li>,
                        ]}
                    </ul>
                </Swipeable>

                <button
                    className={bem('next', [skeleton, isRTL ? 'isRTL' : undefined])}
                    type="button"
                    onClick={this.next}
                    disabled={currentIndex >= slidesWithoutVideo.length - 1}
                    aria-disabled={currentIndex >= slidesWithoutVideo.length - 1}
                />
            </div>
        );
    }
}

ZoomedImageCarousel.propTypes = {
    slides: PropTypes.array,
    currentIndex: PropTypes.number,
    currentOrder: PropTypes.number,
    transition: PropTypes.string,
    transitionDuration: PropTypes.number,
    loading: PropTypes.bool,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    closeZoomModal: PropTypes.func,
    prev: PropTypes.func,
    next: PropTypes.func,
    isRTL: PropTypes.bool,
    zoomedPosition: PropTypes.object,
};

export default ZoomedImageCarousel;
